/**
 * Reconcile a column order persisted before a column existed with the grid's current columns.
 *
 * The data grid appends any column missing from `orderedFields` to the end of the table, so a
 * merchant who has ever reordered their columns would meet a newly added column at the far right
 * rather than where the column definitions place it. Splicing each missing field in beside its
 * nearest already-placed neighbour keeps the saved customisation intact and still introduces new
 * columns where they were designed to sit.
 *
 * Saved fields that are no longer defined are left in place - the grid discards them itself, and
 * removing them here would also drop the fields it owns rather than us, such as the checkbox
 * selection column.
 */
export const mergeSavedColumnOrder = (savedOrder: string[] | undefined, naturalOrder: string[]): string[] | undefined => {
  if (!savedOrder || savedOrder.length === 0) {
    return undefined;
  }

  const merged = [...savedOrder];
  const placed = new Set(savedOrder);

  naturalOrder.forEach((field, index) => {
    if (placed.has(field)) {
      return;
    }

    let insertAt = -1;

    // Prefer sitting just after the closest preceding column that is already placed.
    for (let i = index - 1; i >= 0 && insertAt === -1; i -= 1) {
      const position = merged.indexOf(naturalOrder[i]);
      if (position !== -1) {
        insertAt = position + 1;
      }
    }

    // Nothing precedes it, so sit just before the closest following column instead.
    for (let i = index + 1; i < naturalOrder.length && insertAt === -1; i += 1) {
      const position = merged.indexOf(naturalOrder[i]);
      if (position !== -1) {
        insertAt = position;
      }
    }

    merged.splice(insertAt === -1 ? merged.length : insertAt, 0, field);
    placed.add(field);
  });

  return merged;
};
